import { ChevronDownIcon } from "@heroicons/react/solid";
import produce from "immer";
import { isEmpty, values } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import useSWR, { useSWRConfig } from "swr";
import create from "zustand";
import { persist } from "zustand/middleware";
import AlertMessage from "../../../../components/AlertMessage";
import { useAuthentication } from "../../../../context/authentication";
import { useLocations } from "../../../../context/locations";
import { ModalConfig } from "../../../../context/toast/ToastProvider";
import { whatAreSuperSimulationsText } from "../../../Plans/PricingPage";
import SuperSimulationSidebar from "./SuperSimulationSidebar";
import SuperSimulationsList from "./SuperSimulationsList";

const SuperSimulations: React.FC<RouteComponentProps<{ id: string }>> = (
  props
) => {
  const { mutate } = useSWRConfig();
  const { wrapActionWithAuth } = useAuthentication();
  const { currentLocation, locations } = useLocations();
  const [openSuperSimulationSidebar, setOpenSuperSimulationSidebar] =
    useState(false);
  const [showExplanation, toggleShowExplanation] =
    useSuperSimulationsExplanation((state) => [
      state.showExplanation,
      state.toggleShowExplanation
    ]);
  const [upgradeSimulationsConfig, setUpgradeSimulationsConfig] =
    useState<ModalConfig>({
      open: false,
      resolve: () => {
        toggleSuperSimulationSidebar();
      }
    });

  const location = useMemo(
    () =>
      currentLocation ||
      (props.match.params.id ? locations[props.match.params.id] : undefined),
    [currentLocation, locations, props.match.params.id]
  );

  const hasPendingSuperSimulations = useMemo(
    () =>
      location
        ? !!values(location?.super_simulations).find(
            (item) => item.status === "pending"
          )
        : false,
    [location]
  );

  useSWR(
    hasPendingSuperSimulations
      ? `${props.match.params.id}-refresh-simulations`
      : null,
    () => {
      mutate(props.match.params.id);
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenHidden: false,
      refreshInterval: 30000
    }
  );

  const toggleSuperSimulationSidebar = useCallback(() => {
    if (openSuperSimulationSidebar) {
      setOpenSuperSimulationSidebar(false);
      return;
    }

    setOpenSuperSimulationSidebar(true);
  }, [openSuperSimulationSidebar]);

  if (!location) return null;

  return (
    <>
      <SuperSimulationSidebar
        location={location}
        open={openSuperSimulationSidebar}
        toggle={toggleSuperSimulationSidebar}
        upgradeSimulationsConfig={upgradeSimulationsConfig}
        setUpgradeSimulationsConfig={setUpgradeSimulationsConfig}
      />

      <span className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
        <b>Is this a good location for a new hotspot?</b> Find out by running a{" "}
        <b>super simulation</b> and see how well your <b>PoC Score</b> compares
        to existing hotspots in the network.{" "}
        {!showExplanation && (
          <span
            className="hover:text-hotspotty-500 font-semibold cursor-pointer"
            onClick={toggleShowExplanation}
          >
            Click here to read more{" "}
            <ChevronDownIcon className="h-4 w-4 inline" />
          </span>
        )}
      </span>

      {showExplanation && (
        <AlertMessage
          className="mt-6"
          type="info"
          title="Super Simulations"
          message={
            <>
              <div>{whatAreSuperSimulationsText}</div>
              <div className="mt-2">
                As with RF modeling, this approach has shortcomings too, however
                it is much simpler (No need to enter technical data about the
                antenna, RF frequency, environment, ...) and models reality
                quite well based on what we see in actual network coverage data.
              </div>
            </>
          }
          onClose={toggleShowExplanation}
        />
      )}

      <button
        type="button"
        onClick={wrapActionWithAuth(toggleSuperSimulationSidebar)}
        className="mt-4 justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 flex items-center disabled:opacity-50 disabled:cursor-default disabled:hover:bg-purple-600"
      >
        New super simulation
      </button>

      {!isEmpty(location.super_simulations) && (
        <>
          <div className="mt-6 pb-5 border-b border-gray-200 dark:border-gray-500">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
              <span>Super simulation history</span>
              {!isEmpty(location.super_simulations) && (
                <span className="bg-gray-200 text-gray-500 group-hover:text-gray-700 dark:text-gray-200 dark:bg-gray-600 dark:group-hover:text-gray-200 dark:group-hover:bg-gray-500 hidden ml-3 py-0.5 px-2.5 rounded-full text-xs font-medium md:inline-block">
                  {location.super_simulations.length}
                </span>
              )}
            </h3>
          </div>
          <SuperSimulationsList
            className="mt-4"
            superSimulations={location.super_simulations}
          />
        </>
      )}
    </>
  );
};

export default SuperSimulations;

//
// Utils
//

type SuperSimulationsExplanationState = {
  showExplanation: boolean;
  toggleShowExplanation: () => void;
};

const useSuperSimulationsExplanation = create<SuperSimulationsExplanationState>(
  persist(
    (set, get) => ({
      showExplanation: true,
      toggleShowExplanation: () =>
        set((state) =>
          produce(state, (draft) => {
            draft.showExplanation = !state.showExplanation;
          })
        )
    }),
    {
      name: "super_simulations_explanation"
    }
  )
);
